import { FC, useContext, useMemo } from 'react';
import { BiCaretDown } from 'react-icons/bi';
import {
  ButtonGroup,
  Flex,
  Group,
  IconButton,
  Menu,
  Portal,
  Text,
} from '@chakra-ui/react';

import {
  AssignmentQueue,
  useAssignmentQueues,
} from '~/core/shared/api/middle-office/assignmentQueue';
import { EMPTY_ARR } from '~/core/shared/constants';
import { useApplicationQueueEvents } from '~/core/shared/hooks';
import { DocumentListViewContext } from '../application';
import { Button } from '../Button.component';

type QueueButtonProps = {
  label: string;
  queues: { id: string; name: string; type: string }[];
  onSelect: (queue: { id: string; name: string; type: string }) => void;
  disabled: boolean;
};

const QueueButton: FC<QueueButtonProps> = ({
  label,
  queues,
  onSelect,
  disabled,
}) => {
  if (!queues || queues.length === 0) {
    return null;
  }

  if (queues.length === 1) {
    return (
      <Button
        borderColor="black"
        color="black"
        colorPalette="white"
        disabled={disabled}
        fontSize="sm"
        fontWeight="bold"
        onClick={() => !disabled && onSelect(queues[0])}
        size="xs"
        variant="outline"
        w="fit-content"
      >
        {label}
      </Button>
    );
  }

  return (
    <Group attached>
      <Button
        borderColor="black"
        color="black"
        colorPalette="white"
        disabled={disabled}
        fontSize="sm"
        fontWeight="bold"
        size="xs"
        variant="outline"
        w="fit-content"
      >
        {label}
      </Button>
      <Menu.Root open={!disabled ? undefined : false}>
        <Menu.Trigger asChild>
          <IconButton
            borderColor="black"
            color="black"
            colorPalette="white"
            disabled={disabled}
            fontSize="sm"
            fontWeight="bold"
            lineHeight={0}
            size="xs"
            variant="outline"
          >
            <BiCaretDown />
          </IconButton>
        </Menu.Trigger>
        <Portal>
          <Menu.Positioner>
            <Menu.Content
              opacity={disabled ? 0.4 : 1}
              pointerEvents={disabled ? 'none' : 'auto'}
              py={0}
            >
              {queues.map((q) => (
                <Menu.Item
                  key={q.id}
                  alignItems="center"
                  as={Flex}
                  borderTop="1px solid"
                  borderTopColor="gray.200"
                  color="black"
                  disabled={disabled}
                  justifyContent="space-between"
                  onClick={() => onSelect(q)}
                  value={q.name}
                  w="full"
                >
                  <Text cursor="pointer">{q.name}</Text>
                </Menu.Item>
              ))}
            </Menu.Content>
          </Menu.Positioner>
        </Portal>
      </Menu.Root>
    </Group>
  );
};

export const AssignmentQueueButtons: FC = () => {
  const { config } = useContext(DocumentListViewContext);
  const { assignmentQueues } = useAssignmentQueues(
    config.documentRouteProps?.tenantDocumentDefinition ?? '',
  );
  const { assigning, assignApplication } = useApplicationQueueEvents();

  const handleAssign = (queue: AssignmentQueue) => {
    assignApplication(
      config.documentRouteProps?.tenantDocumentDefinition ?? '',
      queue,
    );
  };

  const groupedQueues = useMemo(
    () =>
      (assignmentQueues ?? (EMPTY_ARR as AssignmentQueue[])).reduce(
        (acc, queue) => {
          const { type } = queue;
          if (!acc[type]) acc[type] = [];
          acc[type].push(queue);
          return acc;
        },
        {} as Record<string, AssignmentQueue[]>,
      ),
    [assignmentQueues],
  );

  return (
    <ButtonGroup gap={4}>
      {Object.entries(groupedQueues).map(([type, queueGroup]) => (
        <QueueButton
          key={type}
          disabled={assigning}
          label={queueGroup[0].name}
          onSelect={handleAssign}
          queues={queueGroup}
        />
      ))}
    </ButtonGroup>
  );
};
